import React, { useEffect, useState, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useWorkflows } from 'features/tours/WorkflowProvider'
import { isWorkflowRunning } from 'features/tours/workflowRunner'
import { getWorkflowByKey } from 'features/tours/tours'
import {
  getRecentWhatsNewRoutine,
  getWhatsNewStatusRoutine,
  markWhatsNewSeenRoutine,
} from './ducks/actions'
import {
  selectWhatsNewRecent,
  selectWhatsNewStatus,
} from './ducks/selectors'
import { selectTutorials } from 'features/tutorials/ducks/selectors'
import { fetchTutorialsRoutine } from 'features/tutorials/ducks/actions'
import {useTutorial} from "../tutorials/context/TutorialContext";

const AUTO_SLIDE_INTERVAL = 5000

const GUIDE_FOR = { 'passport-redesign': 'getting-started.passport-page' }

const guideKeyFor = tourKey => {
  const key = GUIDE_FOR[tourKey] || tourKey
  const guide = key ? getWorkflowByKey(key) : null
  return guide?.steps?.length ? guide.key : null
}

const WhatsNewModal = ({ onClose }) => {
  const dispatch = useDispatch()
  const recent = useSelector(selectWhatsNewRecent)
  const status = useSelector(selectWhatsNewStatus)
  const user = useSelector((state) => state.auth?.user)

  const [currentSlide, setCurrentSlide] = useState(0)
  const [paused, setPaused] = useState(false)
  const timerRef = useRef(null)

  const tutorials = useSelector(selectTutorials) || []
  const { openTutorial } = useTutorial()
  const { startWorkflow, activeWorkflowKey } = useWorkflows()
  const [tutorialsPanelOpen, setTutorialsPanelOpen] = useState(false)


  useEffect(() => {
    dispatch(getRecentWhatsNewRoutine.trigger())
    // dispatch(getWhatsNewStatusRoutine.trigger())
    dispatch(fetchTutorialsRoutine())
  }, [dispatch])

  const slides = recent || []
  const tourItem = slides.find(item => item.tourKey && guideKeyFor(item.tourKey))
  const guideKey = tourItem ? guideKeyFor(tourItem.tourKey) : null

  // auto slide logic
  useEffect(() => {
    if (!slides.length || paused) return
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, AUTO_SLIDE_INTERVAL)
    return () => clearInterval(timerRef.current)
  }, [slides.length, paused])

  if (!status?.shouldPopup || activeWorkflowKey || isWorkflowRunning()) return null

  const handleClose = () => {
    dispatch(markWhatsNewSeenRoutine.trigger())
    onClose?.()
  }

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length)
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)

  return (
    <Overlay>
      <ModalCard>
        <Header>
          <div>
            <Title>
              Welcome back
              {user?.firstName && <UserName>, {user.firstName}!</UserName>}
              {!user?.firstName && '!'}
            </Title>
            <WhatsNewSubtitle>What’s new?</WhatsNewSubtitle>
          </div>
          <CloseBtn onClick={handleClose}>×</CloseBtn>
        </Header>

        <ContentScroll>
          <CarouselWrapper
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <ArrowButton left onClick={prevSlide}>‹</ArrowButton>

            <SlidesContainer>
              {slides.map((item, index) => {
                const offset = (index - currentSlide + slides.length) % slides.length
                let styleProps = {}

                if (offset === 0) {
                  styleProps = {
                    zIndex: 3,
                    scale: 1,
                    opacity: 1,
                    translateX: 0,
                    rotateY: 0,
                    blur: 0,
                    translateZ: 40,
                  }
                } else if (offset === 1) {
                  styleProps = {
                    zIndex: 2,
                    scale: 0.9,
                    opacity: 0.7,
                    translateX: 200,
                    rotateY: -25,
                    blur: 1.5,
                  }
                } else if (offset === slides.length - 1) {
                  styleProps = {
                    zIndex: 2,
                    scale: 0.9,
                    opacity: 0.7,
                    translateX: -200,
                    rotateY: 25,
                    blur: 1.5,
                  }
                } else {
                  styleProps = {
                    zIndex: 1,
                    scale: 0.8,
                    opacity: 0,
                    translateX: offset > 1 ? 400 : -400,
                    rotateY: offset > 1 ? -30 : 30,
                    blur: 3,
                  }
                }

                return (
                  <Slide key={index} {...styleProps}>
                    {item.image && (
                      <SlideImage
                        src={item.image}
                        alt={item.title}
                        onClick={() => {
                          onClose?.()
                          window.dispatchEvent(new Event('openWhatsNewPanel'))
                          setTimeout(() => {
                            window.dispatchEvent(
                              new CustomEvent('whatsnew-updated', { detail: { from: 'modal' } })
                            )
                          }, 150)
                        }}
                      />
                    )}
                  </Slide>
                )
              })}

            </SlidesContainer>

            <ArrowButton right onClick={nextSlide}>›</ArrowButton>
          </CarouselWrapper>

          <Dots>
            {slides.map((_, i) => (
              <Dot key={i} active={i === currentSlide} onClick={() => setCurrentSlide(i)} />
            ))}
          </Dots>

          {guideKey && (
            <TourCta>
              <TourCtaText>
                <strong>See the new Passport design in place.</strong>
                <span>A one-minute guided tour points out what has moved and why.</span>
              </TourCtaText>
              <TourCtaButton
                type='button'
                onClick={() => {
                  dispatch(markWhatsNewSeenRoutine.trigger())
                  onClose?.()
                  startWorkflow(guideKey)
                }}
              >
                Take the guided tour
              </TourCtaButton>
            </TourCta>
          )}

          <TutorialsSection>
            <TutorialsSection>
              <TutorialsHeader>
                Tutorials
                <SeeMore
                  onClick={() => {
                    dispatch(markWhatsNewSeenRoutine.trigger())
                    onClose?.()

                    window.dispatchEvent(new Event('openTutorialsPanel'))

                    setTimeout(() => {
                      window.dispatchEvent(new Event('openTutorialsPanelFromWhatsNew'))
                    }, 200)
                  }}
                >
                  ( see more )
                </SeeMore>
              </TutorialsHeader>

              <TutorialsGrid>
                {tutorials
                  .filter(t => t.is_featured || t.isFeatured)
                  .slice(0, 2)
                  .map((tutorial, i) => (
                    <TutorialCard key={tutorial.id || i} onClick={() => openTutorial(tutorial)}>
                      <h4>{tutorial.title}</h4>
                      <p>{tutorial.description}</p>
                    </TutorialCard>
                  ))}
              </TutorialsGrid>
            </TutorialsSection>
          </TutorialsSection>
        </ContentScroll>
      </ModalCard>
    </Overlay>
  )
}

export default WhatsNewModal

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 18, 24, 0.65);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;

  @media (max-width: 768px) {
    align-items: stretch;
    justify-content: stretch;
  }
`

const ModalCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 36px 40px;
  width: 880px;
  max-width: 94%;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.06);
  animation: fadeIn 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.97); }
    to { opacity: 1; transform: scale(1); }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    padding: 12px 16px 0;
    overflow: hidden;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 12px;

  @media (max-width: 768px) {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #fff;
    padding: 12px 0 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`

const UserName = styled.span`
  font-weight: 900;
  color: #111;
  font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
`

const Title = styled.h2`
  margin: 0;
  font-size: 30px;
  font-weight: 900;
  color: #111;
  letter-spacing: -0.5px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const WhatsNewSubtitle = styled.h3`
  margin-top: 8px;
  margin-bottom: 18px;
  font-size: 22px;
  font-weight: 400;
  color: #555;
  letter-spacing: -0.2px;
  font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #999;
  &:hover { color: #111; }

  @media (max-width: 768px) {
    font-size: 26px;
    align-self: flex-start;
    margin-top: 2px;
  }
`

const ContentScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
`

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 280px;
  margin-bottom: 20px;
  perspective: 1200px;
  overflow: visible;

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 8px;
    perspective: none;
    overflow: hidden;
    padding: 6px 0 0;
  }
`

const SlidesContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  @media (max-width: 768px) {
    height: auto;
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const sheen = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

export const Slide = styled.div`
  position: absolute;
  transition: all 1.6s cubic-bezier(0.19, 1, 0.22, 1);
  transform: ${({translateX, scale, rotateY, translateZ}) =>
    `translateX(calc(${translateX || 0}px - 50%)) translateZ(${translateZ || 0}px) scale(${scale || 1}) rotateY(${rotateY || 0}deg)`};
  opacity: ${({opacity}) => opacity};
  z-index: ${({zIndex}) => zIndex};
  border-radius: 18px;
  overflow: hidden;
  width: 75%;
  left: 50%;
  background: #f7f9ff;
  box-shadow: 0 25px 55px rgba(0, 0, 0, 0.15);
  filter: ${({blur}) => (blur ? `blur(${blur}px) brightness(0.9)` : 'none')};
  transform-origin: center center;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-size: 250% 100%;
    background: linear-gradient(
      130deg,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 0.35) 35%,
      rgba(255, 255, 255, 0.1) 70%,
      rgba(255, 255, 255, 0) 100%
    ) -200% 0;
    mix-blend-mode: screen;
    pointer-events: none;
    opacity: 0;
    border-radius: inherit;
    transition: opacity 0.4s ease, filter 0.4s ease;
  }

  &:hover::after {
    opacity: 0.9;
    filter: brightness(1.2);
    animation: ${sheen} 2.2s ease-in-out forwards;
  }

  @media (max-width: 768px) {
    position: relative;
    left: 0;
    width: 100%;
    transform: none !important;
    opacity: ${({opacity}) => (opacity === 1 ? 1 : 0)};
    display: ${({opacity}) => (opacity === 1 ? 'block' : 'none')};
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  }
`

const SlideImage = styled.img.attrs((props) => ({
  onLoad: (e) => {
    const img = e.target
    const ratio = img.naturalWidth / img.naturalHeight
    img.dataset.fit = ratio > 1.4 ? 'cover' : 'contain'
  }
}))`
  width: 100%;
  height: 100%;
  max-height: 260px;
  object-fit: ${(props) => props['data-fit'] || 'cover'};
  background: #f7f9ff;
  border-radius: 18px;
  display: block;
  transition: transform 0.6s ease, filter 0.6s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    filter: brightness(1.05);
  }

  @media (max-width: 768px) {
    height: auto;
    max-height: 200px;
    object-fit: contain;
    border-radius: 14px;
  }
`


const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ left }) => left && 'left: -28px;'}
  ${({ right }) => right && 'right: -28px;'}
  background: #fff;
  border: none;
  border-radius: 50%;
  font-size: 22px;
  width: 42px;
  height: 42px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  color: #d14724;
  transition: all 0.25s ease;
  z-index: 10;

  &:hover {
    transform: translateY(-50%) scale(1.08);
    background: #fff7f5;
  }

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
    font-size: 18px;
    ${({ left }) => left && 'left: 6px;'}
    ${({ right }) => right && 'right: 6px;'}
  }
`

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 16px;
  gap: 8px;

  @media (max-width: 768px) {
    margin-top: 8px;
    margin-bottom: 12px;
  }
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#111' : '#cfd8dc')};
  cursor: pointer;
  transform: ${({ active }) => (active ? 'scale(1.3)' : 'scale(1)')};

  @media (max-width: 768px) {
    width: 6px;
    height: 6px;
  }
`

const TutorialsSection = styled.div`
  padding-top: 24px;
  margin-top: 8px;
  margin-bottom: 18px;
  font-size: 22px;
  font-weight: 400;
  color: #555;
  letter-spacing: -0.2px;
  font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;

  @media (max-width: 768px) {
    padding-top: 16px;
    margin-bottom: 8px;
  }
`


const TutorialsHeader = styled.h3`
  margin-top: 8px;
  margin-bottom: 18px;
  font-size: 26px;
  font-weight: 400;
  gap: 6px;
  color: #111;
  letter-spacing: -0.2px;
  font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 12px;
  }
`

const SeeMore = styled.span`
  color: #00BCD4;
  font-weight: 600;
  cursor: pointer;
  margin-left: 6px;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`

const TutorialsGrid = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`

const TutorialCard = styled.div`
  flex: 1;
  min-width: 300px;
  background: #f1f6fb;
  border-radius: 14px;
  padding: 22px 26px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.06);
  transition: transform 0.5s ease, box-shadow 0.5s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0,0,0,0.08);
  }

  h4 {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 800;
    color: #111;
    line-height: 1.3;
  }

  p {
    margin: 0;
    font-size: 15px;
    color: #333;
    line-height: 1.4;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    min-width: 0;
    padding: 16px 18px;

    h4 {
      font-size: 16px;
    }

    p {
      font-size: 13px;
    }
  }
`

const TourCta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 0 18px;
  padding: 14px 18px;
  border: 1px solid #cdeff3;
  border-radius: 12px;
  background: #eafafb;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const TourCtaText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 14px;
  color: #475569;

  strong {
    font-size: 15px;
    color: #0C1229;
  }
`

const TourCtaButton = styled.button`
  flex-shrink: 0;
  min-height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 999px;
  background: #33becc;
  color: #ffffff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 120ms ease;

  &:hover { background: #2aa6b3; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 3px; }
`
